import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import {
  FiSearch, FiRefreshCw, FiFileText, FiClock,
  FiMessageSquare, FiCopy, FiThumbsUp, FiThumbsDown, FiCheck,
} from 'react-icons/fi';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import { handleUnauthorized } from '../../utils/apiClient';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import '../coiscan/COIScan.css';
import './PolicyComparison.css';

const PAGE_SIZE    = 10;
const getToken    = (): string => localStorage.getItem('toolboxToken') || '';
const getNcToken  = (): string => localStorage.getItem('amsToken')     || '';

interface NowCertsClient { name: string; databaseId: string; eMail?: string; }

interface PSRecord {
  transactionId: string;
  firstFileName: string;
  created: string;
  userName: string;
  response: string;
  action: string;
  isLiked?: boolean | null;
  totalCount?: number;
}

interface FeedbackPopup {
  record: PSRecord;
  anchor: { top: number; right: number };
  rating: boolean | null;
  text: string;
  error: string;
  submitting: boolean;
}

const formatDate = (d: string) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return d; }
};

const PolicySummaryHistory: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [rows, setRows]               = useState<PSRecord[]>([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [totalCount, setTotalCount]   = useState(0);

  const [preview, setPreview]         = useState<{ open: boolean; record: PSRecord | null }>({ open: false, record: null });

  /* upload to AMS */
  const [uploadAmsOpen,           setUploadAmsOpen]           = useState(false);
  const [uploadAmsRecord,         setUploadAmsRecord]         = useState<PSRecord | null>(null);
  const [uploadAmsClientQuery,    setUploadAmsClientQuery]    = useState('');
  const [uploadAmsClientList,     setUploadAmsClientList]     = useState<NowCertsClient[]>([]);
  const [uploadAmsClientLoading,  setUploadAmsClientLoading]  = useState(false);
  const [uploadAmsSelectedClient, setUploadAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [uploadAmsLoading,        setUploadAmsLoading]        = useState(false);
  const [uploadAmsError,          setUploadAmsError]          = useState('');
  const [uploadAmsSuccess,        setUploadAmsSuccess]        = useState('');
  const [previewAmsMsg,           setPreviewAmsMsg]           = useState('');

  const [fbPopup, setFbPopup]         = useState<FeedbackPopup | null>(null);
  const feedbackRef                   = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        setFbPopup(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchHistory = useCallback(() => {
    setLoading(true);
    axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/OCR/policycomparisonhistory`, {
      params: { pageNumber: page, recordCount: PAGE_SIZE },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        const data: PSRecord[] = Array.isArray(res.data) ? res.data : [];
        setRows(data);
        setTotalCount(data[0]?.totalCount ?? 0);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        if (err.response?.status === 401) handleUnauthorized();
        else showError('Failed to load history.');
      });
  }, [page, navigate, showError]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      r.firstFileName?.toLowerCase().includes(s) ||
      r.userName?.toLowerCase().includes(s) ||
      r.action?.toLowerCase().includes(s)
    );
  });

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const openFeedback = (e: React.MouseEvent, record: PSRecord) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setFbPopup({ record, anchor: { top: rect.bottom + 8, right: window.innerWidth - rect.right }, rating: record.isLiked ?? null, text: '', error: '', submitting: false });
  };

  const onSaveRatings = useCallback((txId: string, isLiked: boolean | null, feedbackText: string) => {
    setFbPopup(p => p ? { ...p, text: '', error: '' } : null);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/OCR/policycomparisonratings`,
      { TransactionId: txId, IsLiked: isLiked, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => {
      showSuccess('Feedback submitted successfully!');
      setFbPopup(null);
    }).catch(err => {
      if (err.response?.status === 401) handleUnauthorized();
    });
  }, [showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    if (!fbPopup) return;
    const txId = fbPopup.record.transactionId;
    const newLiked: boolean | null = fbPopup.record.isLiked === true ? null : true;
    setRows(prev => prev.map(r => r.transactionId === txId ? { ...r, isLiked: newLiked } : r));
    setFbPopup(null);
    onSaveRatings(txId, newLiked, fbPopup.text);
  }, [fbPopup, onSaveRatings]);

  const onThumbsDownClick = useCallback(() => {
    if (!fbPopup) return;
    const txId = fbPopup.record.transactionId;
    const newLiked: boolean | null = fbPopup.record.isLiked === false ? null : false;
    setRows(prev => prev.map(r => r.transactionId === txId ? { ...r, isLiked: newLiked } : r));
    setFbPopup(null);
    onSaveRatings(txId, newLiked, fbPopup.text);
  }, [fbPopup, onSaveRatings]);

  const submitFeedback = useCallback(() => {
    if (!fbPopup) return;
    if (!fbPopup.text.trim()) { setFbPopup(p => p ? { ...p, error: 'Please enter your feedback.' } : null); return; }
    if (fbPopup.text.length > 250) { setFbPopup(p => p ? { ...p, error: 'Feedback must be under 250 characters.' } : null); return; }
    setFbPopup(p => p ? { ...p, submitting: true, error: '' } : null);
    onSaveRatings(fbPopup.record.transactionId, fbPopup.rating, fbPopup.text);
  }, [fbPopup, onSaveRatings]);

  /* upload to AMS — functions */
  const openUploadAms = useCallback((record: PSRecord) => {
    setUploadAmsRecord(record);
    setUploadAmsClientQuery('');
    setUploadAmsClientList([]);
    setUploadAmsSelectedClient(null);
    setUploadAmsError('');
    setUploadAmsSuccess('');
    setUploadAmsOpen(true);
  }, []);

  const getAmsClients = useCallback((query: string) => {
    if (!query.trim()) { setUploadAmsClientList([]); return; }
    setUploadAmsClientLoading(true);
    axios.get(`${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist`, {
      params: { searchText: query, nowCertsToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => { setUploadAmsClientList(r.data || []); setUploadAmsClientLoading(false); })
      .catch(e => { setUploadAmsClientLoading(false); if (e.response?.status === 401) handleUnauthorized(); });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => getAmsClients(uploadAmsClientQuery), 400);
    return () => clearTimeout(t);
  }, [uploadAmsClientQuery, getAmsClients]);

  const onUploadToAMSConfirmed = useCallback(async () => {
    if (!uploadAmsSelectedClient || !uploadAmsRecord) return;
    setUploadAmsLoading(true);
    setUploadAmsError('');
    const ncToken = getNcToken();
    try {
      const response = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/uploadV1`,
        {
          TransactionId: uploadAmsRecord.transactionId,
          FileName: uploadAmsRecord.firstFileName,
          NCPolicyId: '',
          NCInsuredId: uploadAmsSelectedClient.databaseId,
          FileData: '',
          FolderName: 'PolicySummary',
          TargetName: 'PolicySummary',
          NowCertsToken: ncToken,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setUploadAmsLoading(false);
      if (response.data.success) {
        setUploadAmsOpen(false);
        setPreviewAmsMsg('PDF uploaded to AMS successfully.');
        setTimeout(() => setPreviewAmsMsg(''), 3500);
      } else {
        setUploadAmsError(response.data.errorMessage || 'Upload failed.');
      }
    } catch (err: any) {
      setUploadAmsLoading(false);
      if ((err as any).response?.status === 401) handleUnauthorized();
      else setUploadAmsError('Error while uploading PDF to AMS.');
    }
  }, [uploadAmsSelectedClient, uploadAmsRecord]);

  const copyPreview = async () => {
    if (!preview.record) return;
    try {
      const html = await marked(preview.record.response) as string;
      const styled = applyTableStyles(html);
      const plain = htmlToPlain(html);
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([styled], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      showSuccess('Copied to clipboard!');
    } catch { showError('Failed to copy.'); }
  };

  const applyTableStyles = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('table').forEach(t => {
      t.setAttribute('style', 'border-collapse:collapse;width:100%;font-family:Arial,sans-serif;');
      t.querySelectorAll('th').forEach(th => th.setAttribute('style', 'border:2px solid #e3e3e3;padding:8px 10px;'));
      t.querySelectorAll('td').forEach(td => td.setAttribute('style', 'border:1px solid #e3e3e3;padding:7px 10px;'));
    });
    return div.innerHTML;
  };

  const htmlToPlain = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('br,p,h1,h2,h3,h4,h5,h6').forEach(el => {
      el.parentNode?.insertBefore(document.createTextNode('\n'), el);
    });
    return div.textContent || div.innerText || '';
  };

  return (
    <>
      <div className="qp-history">

        {/* ── Header ── */}
        <div className="qp-history__header">
          <h3 className="qp-history__title">Policy Summary History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <button className="csh-refresh-btn" onClick={fetchHistory}>
              <FiRefreshCw size={13} />
              Refresh
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="qp-history__body">
          {loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching policy summary records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search.' : 'No policy summaries yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">File</th>
                    <th className="qp-table__th">Action</th>
                    <th className="qp-table__th">Submitted</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Preview</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.transactionId} className="qp-table__row">
                      <td className="qp-table__td" style={{ color: 'var(--accent-purple-light)', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.firstFileName}>
                        {r.firstFileName || '—'}
                      </td>
                      <td className="qp-table__td">
                        {r.action
                          ? <span className="qp-status-pill" style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>{r.action}</span>
                          : '—'}
                      </td>
                      <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.created)}</td>
                      <td className="qp-table__td">{r.userName}</td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button className="qp-table__action-btn" title="Preview" onClick={() => setPreview({ open: true, record: r })}>
                          <img src={PreviewIcon} alt="Preview" width={22} height={22} />
                        </button>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button
                          className="qp-table__action-btn"
                          title="Feedback"
                          onClick={e => openFeedback(e, r)}
                          style={{ margin: '0 auto' }}
                        >
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = Math.max(1, page - 2) + i;
                  if (p > totalPages) return null;
                  return (
                    <button
                      key={p}
                      className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                      onClick={() => setPage(p)}
                    >{p}</button>
                  );
                })}
                <button className="qp-pagination__btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Feedback popup ── */}
      {fbPopup && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: fbPopup.anchor.top, right: fbPopup.anchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            rows={4}
            placeholder="Enter your feedback here"
            value={fbPopup.text}
            onChange={e => setFbPopup(p => p ? { ...p, text: e.target.value, error: '' } : null)}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${fbPopup.rating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Helpful"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${fbPopup.rating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Not helpful"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {fbPopup.error && <p className="csfb-error">{fbPopup.error}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={() => setFbPopup(null)}>Cancel</button>
            <button className="csfb-btn-submit" disabled={fbPopup.submitting} onClick={submitFeedback}>
              {fbPopup.submitting ? '…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* ── Preview dialog ── */}
      {preview.open && preview.record && ReactDOM.createPortal(
        <>
          {previewAmsMsg && (
            <div className="pc-ams-float-toast">
              <FiCheck size={14} /><span>{previewAmsMsg}</span>
            </div>
          )}
          <div
            className="pc-preview-dialog-backdrop"
            onClick={e => { if (e.target === e.currentTarget) setPreview({ open: false, record: null }); }}
          >
          <div className="pc-preview-dialog">
            <div className="pc-preview-dialog__header">
              <span className="pc-preview-dialog__title">
                {preview.record.action ? `${preview.record.action} — Results` : 'Results'}
              </span>
              <div className="pc-preview-dialog__actions">
                <button className="pc-preview-dialog__btn" onClick={() => preview.record && openUploadAms(preview.record)}>
                  Upload In AMS
                </button>
                <button className="pc-preview-dialog__btn" onClick={copyPreview}>
                  <FiCopy size={12} /> Copy
                </button>
                <button className="pc-preview-dialog__btn pc-preview-dialog__btn--close" onClick={() => setPreview({ open: false, record: null })}>
                  ✕
                </button>
              </div>
            </div>
            <div className="pc-preview-body">
              <div className="pc-markdown-body">
                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                  {preview.record.response}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
        </>,
        document.body
      )}

      {/* ── Upload In AMS Modal ── */}
      {uploadAmsOpen && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={() => setUploadAmsOpen(false)}>
          <div className="csams-modal" onClick={e => e.stopPropagation()}>
            <div className="csams-modal__header">
              <h3 className="csams-modal__title">Upload In AMS</h3>
              <button className="csams-modal__close" onClick={() => setUploadAmsOpen(false)}>✕</button>
            </div>
            <div className="csams-modal__body">
              <label className="csams-label">Search Client</label>
              <div className="csams-search-wrap">
                <input
                  className="csams-search-input"
                  placeholder="Enter client name..."
                  value={uploadAmsClientQuery}
                  onChange={e => { setUploadAmsClientQuery(e.target.value); setUploadAmsSelectedClient(null); }}
                  autoFocus
                />
                {uploadAmsClientLoading && <span className="csams-search-spinner" />}
                {uploadAmsClientList.length > 0 && !uploadAmsSelectedClient && (
                  <ul className="csams-dropdown">
                    {uploadAmsClientList.map(c => (
                      <li
                        key={c.databaseId}
                        className="csams-dropdown__item"
                        onClick={() => { setUploadAmsSelectedClient(c); setUploadAmsClientQuery(c.name); setUploadAmsClientList([]); }}
                      >
                        {c.name}
                        {c.eMail && <span className="csams-dropdown__email">{c.eMail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {uploadAmsSuccess && <p className="csams-success">{uploadAmsSuccess}</p>}
              {uploadAmsError   && <p className="csams-error">{uploadAmsError}</p>}
            </div>
            <div className="csams-modal__footer">
              <button className="csams-btn csams-btn--cancel" onClick={() => setUploadAmsOpen(false)}>Cancel</button>
              <button
                className="csams-btn csams-btn--upload"
                disabled={!uploadAmsSelectedClient || uploadAmsLoading}
                onClick={onUploadToAMSConfirmed}
              >
                {uploadAmsLoading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PolicySummaryHistory;

